import { useState } from 'react';

import StatusCell from '../../../components/Badge/StatusCell';
import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import {
  ActionRow,
  EventRow,
  Heading,
  LoadingText,
  Muted,
  NoteInput,
  Row,
  Section,
  SectionTitle,
} from '../../../components/Detail/DetailParts';
import TableSliderContentWrapper from '../../../components/Table/TableAccessories/TableSliderContentWrapper';
import useGiftDetail from '../../../hooks/dashboard/gifts/useGiftDetail';
import { GIFT_ACTIONS, GiftAction, GiftRow } from '../../../models/gifts/gift.model';
import { capitalize, formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';

const when = (value?: string | null) => (value ? tableFormatDateTimeNG(value) : '—');
const words = (value?: string | null) => (value ? capitalize(value.replace(/_/g, ' ')) : '—');

const GiftDetailSlider: React.FC<{ data: GiftRow }> = ({ data }) => {
  const { gift, loading, saving, act } = useGiftDetail(data.id);
  const [note, setNote] = useState('');
  const current = gift ?? data;
  const actions = (Object.keys(GIFT_ACTIONS) as GiftAction[]).filter((a) =>
    GIFT_ACTIONS[a].allowed(current)
  );
  const amount = Number(current.amount ?? 0);
  const fee = Number(current.fee ?? 0);

  const run = (action: GiftAction) => {
    const { confirm } = GIFT_ACTIONS[action];
    if (confirm && !window.confirm(confirm)) return;
    act(action, note, () => setNote(''));
  };

  return (
    <TableSliderContentWrapper label='Dash gift'>
      <Section>
        <Heading>{current.code}</Heading>
        <StatusCell data={current} statusKey='status' hasDot />
        <Row label='Gift'>
          {current.bill_type ? `${words(current.bill_type)} · ${formatNaira(amount)}` : 'Not chosen yet'}
        </Row>
        <Row label='Fee'>{formatNaira(fee)}</Row>
        <Row label='Sender paid'>{formatNaira(amount + fee)}</Row>
        <Row label='From'>{current.sender_chat_id ?? '—'}</Row>
        <Row label='To'>{current.recipient_chat_id ?? '—'}</Row>
        <Row label='Occasion'>{words(current.occasion)}</Row>
        <Row label='Delivery day'>{when(current.deliver_at)}</Row>
        <Row label='Ready'>{when(current.ready_at)}</Row>
        <Row label='Delivered'>{when(current.delivered_at)}</Row>
        <Row label='Created'>{when(current.created_at)}</Row>
      </Section>

      {gift && (gift.service_id || gift.billers_code) && (
        <Section>
          <SectionTitle>Where it went</SectionTitle>
          <Row label='Provider'>{gift.service_id ?? '—'}</Row>
          <Row label={gift.bill_type === 'ELECTRICITY' ? 'Meter' : 'Phone'}>{gift.billers_code ?? '—'}</Row>
          {gift.meter_type && <Row label='Meter type'>{words(gift.meter_type)}</Row>}
          {gift.customer_name && <Row label='Name on meter'>{gift.customer_name}</Row>}
          <Row label='Delivery attempts'>{gift.delivery_attempts}</Row>
          <Row label='Recipient tries'>{gift.recipient_attempts}</Row>
        </Section>
      )}

      {current.refund_status !== 'NONE' && (
        <Section>
          <SectionTitle>Refund</SectionTitle>
          <Row label='Status'>
            <StatusCell data={current} statusKey='refund_status' />
          </Row>
          <Row label='Reason'>{words(current.refund_reason)}</Row>
          {gift?.refund_amount && <Row label='Amount'>{formatNaira(Number(gift.refund_amount))}</Row>}
          {gift?.refunded_at && <Row label='Refunded'>{when(gift.refunded_at)}</Row>}
        </Section>
      )}

      {actions.length > 0 && (
        <Section>
          <SectionTitle>Actions</SectionTitle>
          {actions.some((a) => GIFT_ACTIONS[a].needsNote) && (
            <NoteInput
              placeholder='Why? (required to freeze, unfreeze or revoke)'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
            />
          )}
          <ActionRow>
            {actions.map((action) => {
              const { label, needsNote } = GIFT_ACTIONS[action];
              const Button = action === 'revoke' ? PrimaryBlackButton : PrimaryWhiteButton;
              const blocked = needsNote && note.trim().length < 3;
              return (
                <Button
                  key={action}
                  type='button'
                  disabled={saving || blocked}
                  title={blocked ? 'Add a note first' : undefined}
                  onClick={() => run(action)}
                >
                  {label}
                </Button>
              );
            })}
          </ActionRow>
          <Muted>Every action is recorded with your admin id. Refunds are never retried automatically.</Muted>
        </Section>
      )}

      {loading && !gift && <LoadingText>Loading…</LoadingText>}

      {gift && gift.transactions.length > 0 && (
        <Section>
          <SectionTitle>Money</SectionTitle>
          {gift.transactions.map((t) => (
            <EventRow key={t.reference}>
              <span>
                {t.type === 'GIFT' ? 'Card charge' : `${words(t.type)} delivery`}
                <br />
                <Muted>{t.reference}</Muted>
              </span>
              <span>
                {formatNaira(Number(t.amount))} · {words(t.status)}
              </span>
              <span>{when(t.created_at)}</span>
            </EventRow>
          ))}
        </Section>
      )}

      {gift && gift.audits.length > 0 && (
        <Section>
          <SectionTitle>History</SectionTitle>
          {gift.audits.map((audit, index) => (
            <EventRow key={index}>
              <span>
                {words(audit.event_type)}
                <br />
                <Muted>{audit.actor ?? 'system'}</Muted>
              </span>
              <span>
                {audit.previous_status || audit.new_status
                  ? `${words(audit.previous_status)} → ${words(audit.new_status)}`
                  : ''}
              </span>
              <span>{when(audit.created_at)}</span>
            </EventRow>
          ))}
        </Section>
      )}
    </TableSliderContentWrapper>
  );
};

export default GiftDetailSlider;
